import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAppAuth } from '../hooks/useAppAuth';
import { useQueryClient } from '@tanstack/react-query';
import { usePipelineNodes, useBuildTree } from '../hooks/usePipelineNodes';
import { useMeasures } from '../hooks/useMeasures';
import { usePipelineNodeRelationships, type PipelineNodeRelationship } from '../hooks/usePipelineNodeRelationships';
import { useIsInDraftMode } from '../hooks/useDraftMode';
import { pipelineNodeService } from '../services/pipelineNode.service';
// Breadcrumb removed - now handled by SemanticLayer wrapper
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Loader2, Search, Box, Calculator, Hash, Activity, Database, Pencil, Save } from 'lucide-react';
import { getPipelineNodeIcon } from '../components/PipelineNodeIcon';
import { SourceIcon } from '../components/SourceIcon';
import { getForeignKeyDisplayName } from '../utils/foreignKeyUtils';
import { useDimensionsList } from '../hooks/useDimension';
import { formatFormula } from '../lib/formatFormula';
import type { PipelineNode, PipelineNodeField, BuildTreeNode, BuildTreeEdge } from '../services/pipelineNode.service';
import type { Measure, MetricType } from '../services/measure.service';
import type { Dimension } from '../services/dimension.service';

function getMetricTypeIcon(metricType: MetricType) {
  switch (metricType) {
    case 'CALCULATED':
      return <Calculator className="h-4 w-4 text-muted-foreground" />;
    case 'COHORT':
      return <Activity className="h-4 w-4 text-muted-foreground" />;
    case 'BASIC':
    default:
      return <Hash className="h-4 w-4 text-muted-foreground" />;
  }
}

function getMetricTypeBadge(metricType: MetricType) {
  switch (metricType) {
    case 'CALCULATED':
      return (
        <Badge variant="outline" className="border-purple-500 text-purple-700 dark:text-purple-400 dark:border-purple-400">
          Calculated
        </Badge>
      );
    case 'COHORT':
      return (
        <Badge variant="outline" className="border-blue-500 text-blue-700 dark:text-blue-400 dark:border-blue-400">
          Cohort
        </Badge>
      );
    case 'BASIC':
    default:
      return (
        <Badge variant="secondary">
          Basic
        </Badge>
      );
  }
}

// Find all SOURCE nodes that feed into a given node by traversing the build tree
// Excludes NODE_RELATIONSHIP edges so we only see sources directly mapped to this entity,
// not sources from parent entities
function findSourceNodesForEntity(
  entityNodeId: string,
  treeNodes: BuildTreeNode[],
  treeEdges: BuildTreeEdge[]
): BuildTreeNode[] {
  const sourceTypes = ['SOURCE', 'SOURCE_FILE', 'SEED_SOURCE'];
  const sources: BuildTreeNode[] = [];
  const visited = new Set<string>();

  function traverse(currentId: string) {
    if (visited.has(currentId)) return;
    visited.add(currentId);

    // Filter out NODE_RELATIONSHIP edges - we only want data pipeline dependencies,
    // not entity relationships (which would include sources from parent entities)
    const incomingEdges = treeEdges.filter(
      e => e.to_id === currentId && e.dependency_type !== 'NODE_RELATIONSHIP'
    );

    for (const edge of incomingEdges) {
      const upstreamNode = treeNodes.find(n => n.id === edge.from_id);
      if (upstreamNode) {
        if (sourceTypes.includes(upstreamNode.type)) {
          if (!sources.find(s => s.id === upstreamNode.id)) {
            sources.push(upstreamNode);
          }
        } else {
          traverse(upstreamNode.id);
        }
      }
    }
  }

  const entityTreeNode = treeNodes.find(n => n.object_id === entityNodeId && n.object_type === 'PipelineNode');
  if (entityTreeNode) {
    traverse(entityTreeNode.id);
  }

  return sources;
}

// Type for tracking field edits
interface FieldEdit {
  label?: string;
  description?: string;
}

// Type for tracking entity-level edits
interface EntityEdit {
  label?: string;
  description?: string;
}

// Type for tracking pending changes per node
interface PendingNodeChanges {
  entity?: EntityEdit;
  fields: Record<string, FieldEdit>;
}

export default function Glossary() {
  const [searchQuery, setSearchQuery] = useState('');
  const isInDraftMode = useIsInDraftMode();
  const { user } = useAppAuth();

  const isAdminOrBuilder = useMemo(() => {
    if (!user) return false;
    const roles = user.roles || [];
    return roles.some((r: any) => {
      const key = (typeof r === 'string' ? r : r?.key || r?.name || '').toLowerCase();
      return key === 'admin' || key === 'builder';
    });
  }, [user]);

  const canViewSources = useMemo(() => {
    if (!user) return false;
    const roles = user.roles || [];
    return roles.some((r: any) => {
      const key = (typeof r === 'string' ? r : r?.key || r?.name || '').toLowerCase();
      return key === 'admin' || key === 'builder' || key === 'readonly';
    });
  }, [user]);
  const queryClient = useQueryClient();

  // Track pending changes to pipeline nodes
  const [pendingChanges, setPendingChanges] = useState<Record<string, PendingNodeChanges>>({});
  const [isSaving, setIsSaving] = useState(false);

  // Edit dialog state for fields
  const [editFieldDialogOpen, setEditFieldDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState<{
    nodeId: string;
    field: PipelineNodeField;
    fieldKey: string;
  } | null>(null);
  const [editFieldLabel, setEditFieldLabel] = useState('');
  const [editFieldDescription, setEditFieldDescription] = useState('');

  // Edit dialog state for entities
  const [editEntityDialogOpen, setEditEntityDialogOpen] = useState(false);
  const [editingEntity, setEditingEntity] = useState<PipelineNode | null>(null);
  const [editEntityLabel, setEditEntityLabel] = useState('');
  const [editEntityDescription, setEditEntityDescription] = useState('');

  // Fetch all pipeline nodes
  const { data: nodesData, isLoading: nodesLoading, error: nodesError } = usePipelineNodes({
    page: 1,
    pageSize: 1000,
  });

  // Fetch build tree to understand source dependencies
  const { data: buildTreeData, isLoading: treeLoading } = useBuildTree({
    enabled: true,
  });

  // Fetch all measures
  const { data: measuresData, isLoading: measuresLoading, error: measuresError } = useMeasures({
    page: 1,
    pageSize: 1000,
  });

  // Fetch dimensions for COHORT formula display and entity drawer
  const { data: dimensionsData, isLoading: dimensionsLoading } = useDimensionsList({
    pageSize: 1000,
  });

  // Fetch relationships for foreign key name resolution
  const { data: relationships, isLoading: relationshipsLoading } = usePipelineNodeRelationships();

  // Build lookup map for nodes
  const nodesById = useMemo(() => {
    const map: Record<string, PipelineNode> = {};
    if (nodesData?.records) {
      for (const node of nodesData.records) {
        map[node.id] = node;
      }
    }
    return map;
  }, [nodesData?.records]);

  // Build lookup map for measures
  const measuresById = useMemo(() => {
    const map: Record<string, Measure> = {};
    if (measuresData?.records) {
      for (const m of measuresData.records) {
        map[m.id] = m;
      }
    }
    return map;
  }, [measuresData?.records]);

  // Build lookup map for dimensions
  const dimensionsById = useMemo(() => {
    const map: Record<string, Dimension> = {};
    if (dimensionsData?.records) {
      for (const d of dimensionsData.records) {
        map[d.id] = d;
      }
    }
    return map;
  }, [dimensionsData?.records]);

  // Build lookup map for relationships
  const relationshipsById = useMemo(() => {
    const map: Record<string, PipelineNodeRelationship> = {};
    if (relationships) {
      for (const rel of relationships) {
        map[rel.id] = rel;
      }
    }
    return map;
  }, [relationships]);

  // Helper function to get relationship by ID
  const getRelationship = useCallback((relationshipId: string) => {
    return relationshipsById[relationshipId];
  }, [relationshipsById]);

  // Helper function to get node name by ID
  const getNodeName = useCallback((nodeId: string) => {
    const node = nodesById[nodeId];
    return node?.label || node?.name;
  }, [nodesById]);

  // Helper function to get entity display name (considering pending changes)
  const getEntityDisplayName = useCallback((entity: PipelineNode) => {
    const pendingEdit = pendingChanges[entity.id]?.entity;
    if (pendingEdit?.label !== undefined) {
      return pendingEdit.label || 'Unnamed Entity';
    }
    return entity.label || entity.name || 'Unnamed Entity';
  }, [pendingChanges]);

  // Helper function to get entity description (considering pending changes)
  const getEntityDescription = useCallback((entity: PipelineNode) => {
    const pendingEdit = pendingChanges[entity.id]?.entity;
    if (pendingEdit?.description !== undefined) {
      return pendingEdit.description;
    }
    return entity.description || '';
  }, [pendingChanges]);

  // Helper function to get field display name (handles FOREIGN_KEY fields)
  const getFieldDisplayName = useCallback((nodeId: string, field: PipelineNodeField) => {
    const fieldKey = field.id || field.name || '';
    const pendingEdit = pendingChanges[nodeId]?.fields[fieldKey];

    if (pendingEdit?.label !== undefined) {
      return pendingEdit.label || 'Unnamed';
    }

    if (field.type === 'FOREIGN_KEY') {
      const displayName = getForeignKeyDisplayName(
        field,
        getRelationship,
        getNodeName
      );
      return displayName || field.label || field.name || 'Unnamed';
    }
    return field.label || field.name || 'Unnamed';
  }, [getRelationship, getNodeName, pendingChanges]);

  // Helper function to get field description (considering pending changes)
  const getFieldDescription = useCallback((nodeId: string, field: PipelineNodeField) => {
    const fieldKey = field.id || field.name || '';
    const pendingEdit = pendingChanges[nodeId]?.fields[fieldKey];

    if (pendingEdit?.description !== undefined) {
      return pendingEdit.description;
    }

    return field.description || '';
  }, [pendingChanges]);

  // Filter to only DIMENSION nodes (entities) - exclude DATE_DIMENSION
  const entities = useMemo(() => {
    const allNodes = nodesData?.records || [];
    const dimensionNodes = allNodes.filter(
      (node: PipelineNode) => node.node_type === 'DIMENSION'
    );

    // Apply search filter
    const filtered = searchQuery.trim()
      ? dimensionNodes.filter((node: PipelineNode) => {
          const label = (node.label || '').toLowerCase();
          const name = (node.name || '').toLowerCase();
          const description = (node.description || '').toLowerCase();
          const query = searchQuery.toLowerCase();
          return label.includes(query) || name.includes(query) || description.includes(query);
        })
      : dimensionNodes;

    // Sort alphabetically by label/name
    return filtered.slice().sort((a, b) =>
      (a.label || a.name || '').localeCompare(b.label || b.name || '', undefined, { sensitivity: 'base' })
    );
  }, [nodesData?.records, searchQuery]);

  // Get source nodes for each entity
  const entitySources = useMemo(() => {
    const map: Record<string, BuildTreeNode[]> = {};
    if (!buildTreeData?.nodes || !buildTreeData?.edges) return map;

    for (const entity of entities) {
      const sources = findSourceNodesForEntity(
        entity.id,
        buildTreeData.nodes,
        buildTreeData.edges
      );
      map[entity.id] = sources;
    }
    return map;
  }, [entities, buildTreeData?.nodes, buildTreeData?.edges]);

  // Filter and sort measures
  const measures = useMemo(() => {
    const allMeasures = measuresData?.records || [];

    const filtered = searchQuery.trim()
      ? allMeasures.filter((measure: Measure) => {
          const name = (measure.name || '').toLowerCase();
          const description = (measure.description || '').toLowerCase();
          const query = searchQuery.toLowerCase();
          return name.includes(query) || description.includes(query);
        })
      : allMeasures;

    return filtered.slice().sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }, [measuresData?.records, searchQuery]);

  // Check if there are any pending changes
  const hasPendingChanges = useMemo(() => {
    return Object.keys(pendingChanges).length > 0;
  }, [pendingChanges]);

  // Count total pending changes
  const pendingChangesCount = useMemo(() => {
    let count = 0;
    for (const nodeChanges of Object.values(pendingChanges)) {
      if (nodeChanges.entity) count++;
      count += Object.keys(nodeChanges.fields).length;
    }
    return count;
  }, [pendingChanges]);

  // Open edit dialog for a field
  const handleFieldClick = useCallback((nodeId: string, field: PipelineNodeField, e: React.MouseEvent) => {
    if (!isInDraftMode) return;
    e.stopPropagation();

    const fieldKey = field.id || field.name || '';
    const pendingEdit = pendingChanges[nodeId]?.fields[fieldKey];

    setEditingField({ nodeId, field, fieldKey });
    setEditFieldLabel(pendingEdit?.label ?? field.label ?? field.name ?? '');
    setEditFieldDescription(pendingEdit?.description ?? field.description ?? '');
    setEditFieldDialogOpen(true);
  }, [isInDraftMode, pendingChanges]);

  // Open edit dialog for an entity
  const handleEntityEditClick = useCallback((entity: PipelineNode, e: React.MouseEvent) => {
    if (!isInDraftMode) return;
    e.stopPropagation();

    const pendingEdit = pendingChanges[entity.id]?.entity;

    setEditingEntity(entity);
    setEditEntityLabel(pendingEdit?.label ?? entity.label ?? entity.name ?? '');
    setEditEntityDescription(pendingEdit?.description ?? entity.description ?? '');
    setEditEntityDialogOpen(true);
  }, [isInDraftMode, pendingChanges]);

  // Save field edit to pending changes
  const handleSaveFieldEdit = useCallback(() => {
    if (!editingField) return;

    const { nodeId, field, fieldKey } = editingField;
    const originalLabel = field.label || field.name || '';
    const originalDescription = field.description || '';

    const labelChanged = editFieldLabel !== originalLabel;
    const descriptionChanged = editFieldDescription !== originalDescription;

    if (!labelChanged && !descriptionChanged) {
      setEditFieldDialogOpen(false);
      setEditingField(null);
      return;
    }

    setPendingChanges(prev => {
      const nodeChanges = prev[nodeId] || { fields: {} };
      const fieldChanges: FieldEdit = {};

      if (labelChanged) {
        fieldChanges.label = editFieldLabel;
      }
      if (descriptionChanged) {
        fieldChanges.description = editFieldDescription;
      }

      return {
        ...prev,
        [nodeId]: {
          ...nodeChanges,
          fields: {
            ...nodeChanges.fields,
            [fieldKey]: {
              ...nodeChanges.fields[fieldKey],
              ...fieldChanges,
            },
          },
        },
      };
    });

    setEditFieldDialogOpen(false);
    setEditingField(null);
  }, [editingField, editFieldLabel, editFieldDescription]);

  // Save entity edit to pending changes
  const handleSaveEntityEdit = useCallback(() => {
    if (!editingEntity) return;

    const originalLabel = editingEntity.label || editingEntity.name || '';
    const originalDescription = editingEntity.description || '';

    const labelChanged = editEntityLabel !== originalLabel;
    const descriptionChanged = editEntityDescription !== originalDescription;

    if (!labelChanged && !descriptionChanged) {
      setEditEntityDialogOpen(false);
      setEditingEntity(null);
      return;
    }

    setPendingChanges(prev => {
      const nodeChanges = prev[editingEntity.id] || { fields: {} };
      const entityChanges: EntityEdit = {};

      if (labelChanged) {
        entityChanges.label = editEntityLabel;
      }
      if (descriptionChanged) {
        entityChanges.description = editEntityDescription;
      }

      return {
        ...prev,
        [editingEntity.id]: {
          ...nodeChanges,
          entity: {
            ...nodeChanges.entity,
            ...entityChanges,
          },
        },
      };
    });

    setEditEntityDialogOpen(false);
    setEditingEntity(null);
  }, [editingEntity, editEntityLabel, editEntityDescription]);

  // Save all pending changes to the server
  const handleSaveAllChanges = useCallback(async () => {
    if (!hasPendingChanges) return;

    setIsSaving(true);
    try {
      for (const [nodeId, nodeChanges] of Object.entries(pendingChanges)) {
        const node = nodesById[nodeId];
        if (!node) continue;

        const updates: Partial<PipelineNode> = { id: nodeId };

        // Apply entity-level changes
        if (nodeChanges.entity) {
          if (nodeChanges.entity.label !== undefined) {
            updates.label = nodeChanges.entity.label;
          }
          if (nodeChanges.entity.description !== undefined) {
            updates.description = nodeChanges.entity.description;
          }
        }

        // Apply field-level changes
        if (Object.keys(nodeChanges.fields).length > 0) {
          updates.fields = (node.fields || []).map(field => {
            const fieldKey = field.id || field.name || '';
            const fieldEdit = nodeChanges.fields[fieldKey];

            if (fieldEdit) {
              return {
                ...field,
                ...(fieldEdit.label !== undefined && { label: fieldEdit.label }),
                ...(fieldEdit.description !== undefined && { description: fieldEdit.description }),
              };
            }
            return field;
          });
        }

        await pipelineNodeService.save(updates);
      }

      setPendingChanges({});
      queryClient.invalidateQueries({ queryKey: ['pipeline-nodes'] });
    } catch (error) {
      console.error('Failed to save changes:', error);
    } finally {
      setIsSaving(false);
    }
  }, [hasPendingChanges, pendingChanges, nodesById, queryClient]);

  const isLoading = nodesLoading || treeLoading || measuresLoading || dimensionsLoading || relationshipsLoading;
  const hasError = nodesError || measuresError;

  return (
    <div className="w-full h-full flex flex-col bg-background overflow-hidden">
      {/* Search & Actions Bar */}
      <div className="flex items-center justify-end w-full px-6 py-3 border-b border-border bg-background flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search glossary..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-64 pl-9"
            />
          </div>
          {hasPendingChanges && isInDraftMode && (
            <Button
              onClick={handleSaveAllChanges}
              disabled={isSaving}
              className="gap-2"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save Changes ({pendingChangesCount})
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-8 min-h-0">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Loading glossary...</p>
            </div>
          </div>
        ) : hasError ? (
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-destructive text-sm">
              Failed to load glossary data. Please try again.
            </p>
          </div>
        ) : (
          <div className="space-y-12 max-w-5xl">
            {/* Section 1: Key Business Concepts (Entities) */}
            <section>
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <Box className="h-5 w-5" />
                  Key Business Concepts
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  These are the core entities that exist in Pliable and what we know about them. Each entity represents a key business concept with its attributes and data sources.
                </p>
              </div>

              {entities.length === 0 ? (
                <div className="p-8 text-center border rounded-md">
                  <p className="text-muted-foreground">No entities found.</p>
                </div>
              ) : (
                <Accordion type="multiple" className="space-y-2">
                  {entities.map((entity) => {
                    const sources = entitySources[entity.id] || [];
                    // Sort fields alphabetically by label/name
                    const fields = [...(entity.fields || [])].sort((a, b) => {
                      const aName = (a.label || a.name || '').toLowerCase();
                      const bName = (b.label || b.name || '').toLowerCase();
                      return aName.localeCompare(bName);
                    });
                    const displayName = getEntityDisplayName(entity);
                    const displayDescription = getEntityDescription(entity);
                    const hasEntityPendingEdit = !!pendingChanges[entity.id]?.entity;

                    return (
                      <AccordionItem
                        key={entity.id}
                        value={entity.id}
                        className="border rounded-lg px-4"
                      >
                        <AccordionTrigger className="hover:no-underline">
                          <div className="flex items-center gap-3 text-left flex-1">
                            {getPipelineNodeIcon(entity.node_type)}
                            <div className="flex-1 min-w-0">
                              {/* Entity Name - Editable */}
                              <div
                                className={`font-medium flex items-center gap-2 group/name ${isInDraftMode ? 'cursor-pointer' : ''}`}
                                onClick={(e) => handleEntityEditClick(entity, e)}
                              >
                                <span>{displayName}</span>
                                {isInDraftMode && (
                                  <Pencil className="h-3 w-3 text-muted-foreground opacity-0 group-hover/name:opacity-100 transition-opacity" />
                                )}
                                {hasEntityPendingEdit && (
                                  <Badge variant="outline" className="text-xs px-1 py-0">
                                    edited
                                  </Badge>
                                )}
                              </div>
                              {/* Entity Description - Editable */}
                              <div
                                className={`text-sm text-muted-foreground font-normal flex items-center gap-2 group/desc ${isInDraftMode ? 'cursor-pointer' : ''}`}
                                onClick={(e) => handleEntityEditClick(entity, e)}
                              >
                                <span className={!displayDescription ? 'italic' : ''}>
                                  {displayDescription || 'No description'}
                                </span>
                                {isInDraftMode && (
                                  <Pencil className="h-3 w-3 text-muted-foreground opacity-0 group-hover/desc:opacity-100 transition-opacity" />
                                )}
                              </div>
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="pt-2 pb-4 space-y-6">
                            {/* Data Sources */}
                            {canViewSources && (
                            <div>
                              <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                                <Database className="h-4 w-4" />
                                Data Sources
                              </h4>
                              {sources.length === 0 ? (
                                <p className="text-sm text-muted-foreground">No data sources identified.</p>
                              ) : (
                                <div className="flex flex-wrap gap-2">
                                  {sources.map((source) => {
                                    // Look up the actual pipeline node to get source_id
                                    const sourceNode = nodesById[source.object_id];
                                    // Use source_id if available, otherwise fallback to 'snowflake'
                                    const sourceId = sourceNode?.source_id || 'snowflake';

                                    return (
                                      <div
                                        key={source.id}
                                        className="flex items-center gap-2 bg-muted/50 rounded-md px-3 py-1.5"
                                      >
                                        <SourceIcon sourceId={sourceId} size={16} />
                                        <span className="text-sm">{source.title}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                            )}

                            {/* Columns/Fields */}
                            <div>
                              <h4 className="text-sm font-medium text-foreground mb-2">
                                Columns ({fields.length})
                              </h4>
                              {fields.length === 0 ? (
                                <p className="text-sm text-muted-foreground">No columns defined.</p>
                              ) : (
                                <div className="border rounded-md overflow-hidden">
                                  <table className="w-full text-sm">
                                    <thead className="bg-muted/50">
                                      <tr>
                                        <th className="text-left px-3 py-2 font-medium">Column</th>
                                        <th className="text-left px-3 py-2 font-medium">Type</th>
                                        <th className="text-left px-3 py-2 font-medium">Description</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y">
                                      {fields.map((field: PipelineNodeField, index: number) => {
                                        const fieldKey = field.id || field.name || String(index);
                                        const hasPendingEdit = !!pendingChanges[entity.id]?.fields[fieldKey];
                                        const fieldDisplayName = getFieldDisplayName(entity.id, field);
                                        const fieldDisplayDescription = getFieldDescription(entity.id, field);

                                        return (
                                          <tr
                                            key={fieldKey}
                                            className={`group ${isInDraftMode ? 'cursor-pointer hover:bg-muted/50' : ''}`}
                                            onClick={(e) => handleFieldClick(entity.id, field, e)}
                                          >
                                            <td className="px-3 py-2 font-mono text-xs">
                                              <div className="flex items-center gap-2">
                                                <span>{fieldDisplayName}</span>
                                                {isInDraftMode && (
                                                  <Pencil className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                                                )}
                                                {hasPendingEdit && (
                                                  <Badge variant="outline" className="text-xs px-1 py-0">
                                                    edited
                                                  </Badge>
                                                )}
                                              </div>
                                            </td>
                                            <td className="px-3 py-2 text-muted-foreground">
                                              {field.type || '-'}
                                            </td>
                                            <td className="px-3 py-2 text-muted-foreground">
                                              <span className={!fieldDisplayDescription ? 'italic' : ''}>
                                                {fieldDisplayDescription || 'No description'}
                                              </span>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>

                            {/* Link to Entity Detail - only shown to Admin/Builder */}
                            {isAdminOrBuilder && (
                              <div className="pt-2">
                                <Link
                                  to={`/develop/entities/${entity.id}`}
                                  className="text-sm text-primary hover:underline"
                                >
                                  View full entity configuration
                                </Link>
                              </div>
                            )}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              )}
            </section>

            {/* Section 2: Metrics */}
            <section>
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <Calculator className="h-5 w-5" />
                  Metrics
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  All the metrics defined in the system, including how they are calculated.
                </p>
              </div>

              {measures.length === 0 ? (
                <div className="p-8 text-center border rounded-md">
                  <p className="text-muted-foreground">No metrics found.</p>
                </div>
              ) : (
                <div className="border rounded-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left px-4 py-3 font-medium">Metric</th>
                        <th className="text-left px-4 py-3 font-medium">Type</th>
                        <th className="text-left px-4 py-3 font-medium">Calculation</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {measures.map((measure: Measure) => (
                        <tr key={measure.id} className="hover:bg-muted/25">
                          <td className="px-4 py-3">
                            <div className="flex items-start gap-2">
                              <div className="flex-shrink-0 mt-0.5">
                                {getMetricTypeIcon(measure.metric_type)}
                              </div>
                              <div>
                                <div className="font-medium">{measure.name}</div>
                                <div className={`text-xs text-muted-foreground mt-0.5 ${!measure.description ? 'italic' : ''}`}>
                                  {measure.description || 'No description'}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {getMetricTypeBadge(measure.metric_type)}
                          </td>
                          <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                            {formatFormula(measure, nodesById, measuresById, dimensionsById)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {measures.length > 0 && isAdminOrBuilder && (
                <div className="mt-4">
                  <Link
                    to="/develop/metrics"
                    className="text-sm text-primary hover:underline"
                  >
                    View all metrics in detail
                  </Link>
                </div>
              )}
            </section>
          </div>
        )}
      </div>

      {/* Edit Field Dialog */}
      <Dialog open={editFieldDialogOpen} onOpenChange={setEditFieldDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Column</DialogTitle>
            <DialogDescription>
              {editingField?.field.type === 'FOREIGN_KEY' || editingField?.field.type === 'DENORMALIZED'
                ? 'This is a relationship column.'
                : 'Update the display name and description for this column.'}
            </DialogDescription>
          </DialogHeader>
          {editingField?.field.type === 'FOREIGN_KEY' || editingField?.field.type === 'DENORMALIZED' ? (
            <DialogBody>
              <p className="text-sm text-muted-foreground mb-4">
                Relationship columns are derived from entity relationships and cannot be edited directly from the glossary.
                To modify this column, please visit the entity configuration screen where you can manage relationships and their mapped columns.
              </p>
              <Link
                to={`/develop/entities/${editingField?.nodeId}`}
                className="text-sm text-primary hover:underline"
                onClick={() => setEditFieldDialogOpen(false)}
              >
                Go to entity configuration →
              </Link>
            </DialogBody>
          ) : (
            <>
              <DialogBody className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="field-label">Column Name</Label>
                  <Input
                    id="field-label"
                    value={editFieldLabel}
                    onChange={(e) => setEditFieldLabel(e.target.value)}
                    placeholder="Enter column name"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="field-description">Description</Label>
                  <Textarea
                    id="field-description"
                    value={editFieldDescription}
                    onChange={(e) => setEditFieldDescription(e.target.value)}
                    placeholder="Enter column description"
                    rows={3}
                  />
                </div>
              </DialogBody>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditFieldDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveFieldEdit}>
                  Save
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Entity Dialog */}
      <Dialog open={editEntityDialogOpen} onOpenChange={setEditEntityDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Entity</DialogTitle>
            <DialogDescription>
              Update the name and description for this entity.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="entity-label">Entity Name</Label>
              <Input
                id="entity-label"
                value={editEntityLabel}
                onChange={(e) => setEditEntityLabel(e.target.value)}
                placeholder="Enter entity name"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="entity-description">Description</Label>
              <Textarea
                id="entity-description"
                value={editEntityDescription}
                onChange={(e) => setEditEntityDescription(e.target.value)}
                placeholder="Enter entity description"
                rows={3}
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditEntityDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEntityEdit}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
